import apiClient from './apiClient.js'

const CACHE_DURATION = parseInt(import.meta?.env?.VITE_LANGUAGE_CACHE_DURATION || '60000') // Default 1 minute

/**
 * Expected language object structure:
 * {
 *   id: string|number,
 *   code: string,           // Language code (e.g., 'es', 'en', 'fr')
 *   name: string,           // Language name (e.g., 'Español', 'English')
 *   flag: string,           // Flag emoji or icon
 *   isDefault: boolean,     // Whether this is the default language
 *   isActive: boolean,      // Whether this language is active (for frontend use only)
 *   headerText: string,     // Header text (e.g., "Viendo en Español")
 *   emptyText: string       // Text to show when field is empty
 * }
 */

import { Cache } from '../utils/Cache.js'
const cache = new Cache()

/**
 * Servicio para gestionar los idiomas
 */
export const languageService = {

  async getLanguageById(id, forceRefresh = false) {
    try {
      if (!forceRefresh) {
        const cachedData = await cache.get(`language_${id}`)
        if (cachedData) {
          return cachedData
        }
      }

      const response = await apiClient.get(`/languages/${id}`)

      // Cache the result
      cache.set(`language_${id}`, response.data)

      return response.data
    } catch (error) {
      console.error(`Error al obtener idioma ${id}:`, error)
      throw error
    }
  },

  async getAllLanguages(forceRefresh = false) {
    try {
      // Check cache first
      if (!forceRefresh) {
        const cachedData = await cache.get('allLanguages')
        if (cachedData) {
          return cachedData
        }
      }

      const response = await apiClient.get('/languages')

      // Cache the result
      cache.set('allLanguages', response.data)

      return response.data
    } catch (error) {
      console.error('Error al obtener todos los idiomas:', error)
      throw error
    }
  },

  async getActiveLanguages(forceRefresh = false) {
    try {
      if (!forceRefresh) {
        const cachedData = await cache.get('activeLanguages')
        if (cachedData) {
          return cachedData
        }
      }

      const response = await apiClient.get('/languages/active')

      // Cache the result
      cache.set('activeLanguages', response.data)

      return response.data
    } catch (error) {
      console.error('Error al obtener idiomas activos:', error)
      throw error
    }
  },

  async toggleLanguageActive(id) {
    try {
      const response = await apiClient.patch(`/languages/${id}/toggle-active`)

      // Invalidate cache
      cache.clear()

      return response.data
    } catch (error) {
      console.error(`Error al activar/desactivar idioma ${id}:`, error)
      throw error
    }
  },

  /**
   * Crea un nuevo idioma
   */
  async createLanguage(languageData) {
    try {
      const response = await apiClient.post('/languages', languageData)

      // Invalidate cache
      cache.clear()

      return response.data
    } catch (error) {
      console.error('Error al crear idioma:', error)
      throw error
    }
  },

  /**
   * Actualiza un idioma existente
   */
  async updateLanguage(id, languageData) {
    try {
      const response = await apiClient.put(`/languages/${id}`, languageData)

      // Invalidate cache
      cache.clear()

      return response.data
    } catch (error) {
      console.error(`Error al actualizar idioma ${id}:`, error)
      throw error
    }
  },

  /**
   * Elimina un idioma
   */
  async deleteLanguage(id) {
    try {
      const response = await apiClient.delete(`/languages/${id}`)

      // Invalidate cache
      cache.clear()

      return response.data
    } catch (error) {
      console.error(`Error al eliminar idioma ${id}:`, error)
      throw error
    }
  },

  /**
   * Obtiene el idioma predeterminado
   */
  async getDefaultLanguage(forceRefresh = false) {
    try {
      if (!forceRefresh) {
        const cachedData = await cache.get('defaultLanguage')
        if (cachedData) {
          return cachedData
        }
      }

      const response = await apiClient.get('/languages/default')

      // Cache the result
      cache.set('defaultLanguage', response.data)

      return response.data
    } catch (error) {
      console.error('Error al obtener idioma predeterminado:', error)
      throw error
    }
  },

  /**
   * Limpia la caché
   */
  clearCache() {
    cache.clear()
  }
}

export default languageService
